import React, { useCallback, useEffect, useState } from "react";

import HttpManager from "../../../../services/HttpManager";
import { ProductRow } from "../../../../services/v2/inventory.service";
import ReasonPrompt from "../../../../components/kits/reasonPrompt";
import { Empty, Loading, PER_PAGE_DEFAULT, Pager, saveBlob, ui, useCampus, useDebounced, useIdleLocked, usePerms } from "../kits/shared";
import CatalogoViejo from "./catalogoViejo";
import ProductForm from "./productForm";
import ProductPicker from "./productPicker";

/* Tienda → Productos (sección "products"): catálogo, kits y uniformes compuestos, tallas y kits por curso. */

type TabKey = "kit" | "uniform" | "article" | "digital" | "service" | "courses" | "prices" | "legacy";
const TABS: { key: TabKey; label: string }[] = [
	{ key: "kit", label: "Kits" },
	{ key: "uniform", label: "Uniformes" },
	{ key: "article", label: "Artículos" },
	{ key: "digital", label: "Digitales" },
	{ key: "service", label: "Servicios y cobros" },
	{ key: "courses", label: "Kits por Curso" },
	{ key: "prices", label: "Precios por aprobar" },
	{ key: "legacy", label: "Catálogo viejo" }
];
const SECTION = "products";

/** Miniatura de la foto del producto (viaja autenticada, por eso se descarga como blob). */
const Thumb: React.FC<{ campusId: number; productId: number; has: boolean }> = ({ campusId, productId, has }) => {
	const [url, setUrl] = useState<string | null>(null);
	useEffect(() => {
		if (!has) return;
		let objectUrl: string | null = null;
		let alive = true;
		HttpManager.getInstance().inventoryService.photoBlob(campusId, productId)
			.then((response) => { if (!alive) return; objectUrl = URL.createObjectURL(response.data); setUrl(objectUrl); })
			.catch(() => undefined);
		return () => { alive = false; if (objectUrl) URL.revokeObjectURL(objectUrl); };
	}, [campusId, productId, has]);
	return <div style={{ width: "44px", height: "44px", borderRadius: "8px", background: "var(--wa-papel-2)", display: "flex", alignItems: "center", justifyContent: "center", overflow: "hidden", flex: "0 0 44px" }}>
		{url ? <img src={url} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} /> : <span style={{ fontSize: "10px", color: "var(--wa-tenue)" }}>sin foto</span>}
	</div>;
};

const Productos: React.FC = () => {
	const { campus, campusId, currency } = useCampus();
	const idleLocked = useIdleLocked();
	const perms: any = usePerms(SECTION);
	const canCreate = !!perms.create;
	const canUpdate = !!perms.update;
	const http = HttpManager.getInstance();

	const [tabKey, setTabKey] = useState<TabKey>("kit");
	const [catalogs, setCatalogs] = useState<any>(null);
	const [query, setQuery] = useState<string>("");
	const debouncedQuery = useDebounced(query);
	const [includeDeprecated, setIncludeDeprecated] = useState<boolean>(false);
	const [rows, setRows] = useState<ProductRow[]>([]);
	const [courseRows, setCourseRows] = useState<any[]>([]);
	const [total, setTotal] = useState<number>(0);
	const [lastPage, setLastPage] = useState<number>(1);
	const [page, setPage] = useState<number>(1);
	const [loading, setLoading] = useState<boolean>(false);
	const [error, setError] = useState<string | null>(null);
	const [counts, setCounts] = useState<Record<string, number>>({});

	const [formProduct, setFormProduct] = useState<any | null | undefined>(undefined); // undefined = cerrado, null = alta
	const [sizesProduct, setSizesProduct] = useState<any | null>(null);
	const [deprecating, setDeprecating] = useState<ProductRow | null>(null);
	const [modalBusy, setModalBusy] = useState<boolean>(false);
	const [modalError, setModalError] = useState<string | null>(null);
	const [detail, setDetail] = useState<any | null>(null);
	const [courseEditing, setCourseEditing] = useState<any | null>(null);

	useEffect(() => {
		if (!campusId) return;
		http.inventoryService.catalogs(campusId).then((response) => setCatalogs(response.data)).catch(() => setCatalogs(null));
	}, [campusId]);

	const load = useCallback(async () => {
		if (!campusId) return;
		setLoading(true);
		setError(null);
		try {
			if (tabKey === "prices" || tabKey === "legacy") {
				setLoading(false);
				return;
			}
			if (tabKey === "courses") {
				const response = await http.inventoryService.courseKits(campusId);
				setCourseRows(response.data || []);
				setTotal((response.data || []).length);
				setLastPage(1);
			} else {
				const response = await http.inventoryService.list(campusId, { kinds: [tabKey], q: debouncedQuery.trim(), includeDeprecated: includeDeprecated ? 1 : 0 }, page, PER_PAGE_DEFAULT);
				setRows(response.data.data || []);
				setTotal(response.data.total || 0);
				setLastPage(response.data.last_page || 1);
			}
		} catch (requestError: any) {
			setError(requestError?.response?.data?.message || "No se pudo cargar el catálogo.");
			setRows([]);
		} finally {
			setLoading(false);
		}
	}, [campusId, tabKey, debouncedQuery, includeDeprecated, page]);

	useEffect(() => { load(); }, [load]);

	const refreshCounts = useCallback(() => {
		if (!campusId) return;
		TABS.filter((tab) => tab.key !== "courses" && tab.key !== "prices" && tab.key !== "legacy").forEach((tab) => {
			http.inventoryService.list(campusId, { kinds: [tab.key] }, 1, 1)
				.then((response) => setCounts((previous) => ({ ...previous, [tab.key]: response.data.total || 0 })))
				.catch(() => undefined);
		});
	}, [campusId]);
	useEffect(() => { refreshCounts(); }, [refreshCounts]);

	const after = async () => { await load(); refreshCounts(); };

	const openEdit = async (row: ProductRow) => {
		setModalError(null);
		try {
			const response = await http.inventoryService.get(campusId, row.id);
			setFormProduct(response.data);
		} catch (requestError) {
			setError("No se pudo abrir el producto.");
		}
	};

	const saveProduct = async (data: Record<string, any>, photo: File | null) => {
		setModalBusy(true);
		setModalError(null);
		try {
			if (formProduct) await http.inventoryService.update(campusId, formProduct.id, data, photo);
			else await http.inventoryService.create(campusId, data, photo);
			setFormProduct(undefined);
			await after();
		} catch (requestError: any) {
			const payload = requestError?.response?.data;
			setModalError((payload?.errors && Object.values(payload.errors)[0]) ? String((Object.values(payload.errors)[0] as any)[0]) : (payload?.message || "No se pudo guardar."));
		} finally {
			setModalBusy(false);
		}
	};

	const saveSizes = async (sizes: string[]) => {
		setModalBusy(true);
		setModalError(null);
		try {
			await http.inventoryService.createVariants(campusId, sizesProduct.id, sizes);
			setSizesProduct(null);
			await after();
		} catch (requestError: any) {
			setModalError(requestError?.response?.data?.message || "No se pudieron crear las tallas.");
		} finally {
			setModalBusy(false);
		}
	};

	const onDeprecate = async (reason: string) => {
		if (!deprecating) return;
		setModalBusy(true);
		setModalError(null);
		try {
			if (deprecating.deprecatedAt) await http.inventoryService.restore(campusId, deprecating.id, reason);
			else await http.inventoryService.deprecate(campusId, deprecating.id, reason);
			setDeprecating(null);
			await after();
		} catch (requestError: any) {
			setModalError(requestError?.response?.data?.message || "No se pudo completar la operación.");
		} finally {
			setModalBusy(false);
		}
	};

	const openDetail = async (row: ProductRow) => {
		try {
			const response = await http.inventoryService.get(campusId, row.id);
			setDetail(response.data);
		} catch (requestError) {
			setError("No se pudo abrir la ficha.");
		}
	};

	// ---------------------------------------------------------------- tablas

	const productsTable = () => <table style={{ width: "100%", borderCollapse: "collapse" }}>
		<thead><tr>
			<th style={ui.th}></th>
			<th style={ui.th}>Producto</th>
			<th style={ui.th}>Código</th>
			<th style={{ ...ui.th, ...ui.right }}>Precio</th>
			{tabKey === "kit" || tabKey === "uniform" ? <th style={ui.th}>Versión</th> : null}
			{tabKey === "article" ? <th style={{ ...ui.th, ...ui.right }}>Existencia</th> : null}
			<th style={ui.th}>Tallas</th>
			<th style={ui.th}></th>
		</tr></thead>
		<tbody>{rows.map((row) => <tr key={row.id} style={{ opacity: row.deprecatedAt ? 0.6 : 1 }}>
			<td style={ui.td}><Thumb campusId={campusId} productId={row.id} has={row.hasPhoto} /></td>
			<td style={ui.td}>
				<div style={{ fontWeight: 600, cursor: "pointer" }} onClick={() => openDetail(row)}>{row.name}</div>
				<div style={ui.tiny}>{row.deprecatedAt ? "Dado de baja el " + row.deprecatedAt : row.kindLabel}</div>
			</td>
			<td style={{ ...ui.td, whiteSpace: "nowrap" }}>
				<div style={{ fontFamily: "monospace", fontSize: "12px" }}>{row.waCode || "—"}</div>
				{row.barcode ? <div style={ui.tiny}>barras {row.barcode}</div> : null}
			</td>
			<td style={{ ...ui.td, ...ui.right, whiteSpace: "nowrap" }}>
				{row.pricePending ? <span style={{ color: "var(--wa-oro)", fontWeight: 600, fontSize: "12px" }}>Precio por aprobar</span> : <>{currency}{row.price.toFixed(2)}</>}
				{row.suggestedPrice ? <div style={ui.tiny}>sugerido {currency}{Number(row.suggestedPrice).toFixed(2)}</div> : null}
			</td>
			{tabKey === "kit" || tabKey === "uniform" ? <td style={ui.td}>
				{row.versionLabel ? <><div>{row.versionLabel}</div>{row.tier ? <div style={ui.tiny}>{row.tier}</div> : null}</> : <span style={{ color: "var(--wa-oro)", fontSize: "12px" }}>Sin componentes</span>}
			</td> : null}
			{tabKey === "article" ? <td style={{ ...ui.td, ...ui.right }}>
				{row.trackStock ? row.onHand : <span style={ui.muted}>por talla</span>}
			</td> : null}
			<td style={ui.td}>{row.variantCount > 0 ? <span>{row.variantCount} tallas</span> : (row.sizeType && row.sizeType !== "none" ? <span style={ui.muted}>sin crear</span> : <span style={ui.muted}>—</span>)}</td>
			<td style={ui.td}><div style={{ display: "flex", gap: "6px", whiteSpace: "nowrap", justifyContent: "flex-end" }}>
				{canUpdate ? <button type="button" className={ui.btn} disabled={idleLocked} onClick={() => openEdit(row)}>Editar</button> : null}
				{canUpdate && row.sizeType && row.sizeType !== "none" && !row.modelId ? <button type="button" className={ui.btn} disabled={idleLocked} onClick={() => { setModalError(null); setSizesProduct(row); }}>Tallas</button> : null}
				{canUpdate ? <button type="button" className={"btn btn-sm rounded-pill " + (row.deprecatedAt ? "btn-outline-primary" : "btn-outline-danger")} disabled={idleLocked} onClick={() => { setModalError(null); setDeprecating(row); }}>{row.deprecatedAt ? "Reactivar" : "Baja"}</button> : null}
			</div></td>
		</tr>)}</tbody>
	</table>;

	const coursesTable = () => <table style={{ width: "100%", borderCollapse: "collapse" }}>
		<thead><tr>
			<th style={ui.th}>Curso</th>
			<th style={ui.th}>Kits que se ofrecen</th>
			<th style={ui.th}></th>
		</tr></thead>
		<tbody>{courseRows.map((course) => <tr key={course.courseId}>
			<td style={{ ...ui.td, fontWeight: 600, whiteSpace: "nowrap" }}>{course.courseBrief}</td>
			<td style={ui.td}>
				{course.kits.length < 1 ? <span style={ui.muted}>Sin kit asignado</span>
					: course.kits.map((kit: any, index: number) => <span key={kit.kitArticleId}
						style={{ display: "inline-block", background: kit.required ? "var(--wa-acento-suave)" : "var(--wa-papel-2)", color: kit.required ? "var(--wa-acento-hover)" : "var(--wa-apagado)", borderRadius: "999px", padding: "2px 10px", fontSize: "12px", marginRight: "6px" }}>
						{index + 1}. {kit.name} · {currency}{kit.price.toFixed(2)} · {kit.required ? "obligatorio" : "recomendado"}
					</span>)}
			</td>
			<td style={{ ...ui.td, ...ui.right }}>
				{canUpdate ? <button type="button" className={ui.btn} disabled={idleLocked} onClick={() => setCourseEditing({ ...course, draft: [...course.kits] })}>Editar</button> : null}
			</td>
		</tr>)}</tbody>
	</table>;

	if (!campusId) return <div style={ui.page}>Selecciona un plantel.</div>;

	return <div style={ui.page}>
		<div style={{ display: "flex", alignItems: "center", gap: "12px", flexWrap: "wrap" }}>
			<h2 style={ui.title}>Productos</h2>
			{canCreate && tabKey !== "courses" ? <button type="button" className="btn btn-primary rounded-pill" disabled={idleLocked} onClick={() => { setModalError(null); setFormProduct(null); }}>Nuevo producto</button> : null}
		</div>
		<div style={ui.tabs}>
			{TABS.filter((tab) => tab.key !== "prices" || catalogs?.isSuperAdmin).map((tab) => <button key={tab.key} type="button" disabled={idleLocked}
				className={"btn rounded-pill " + (tab.key === tabKey ? "btn-primary" : "btn-outline-secondary")}
				onClick={() => { setTabKey(tab.key); setPage(1); setRows([]); }}>
				{tab.label}{tab.key === "prices" ? (catalogs?.pendingPrices ? <span style={ui.badge(tab.key === tabKey)}>{catalogs.pendingPrices}</span> : null)
					: counts[tab.key] !== undefined && tab.key !== "courses" ? <span style={ui.badge(tab.key === tabKey)}>{counts[tab.key]}</span> : null}
			</button>)}
		</div>

		{tabKey === "legacy" ? <CatalogoViejo campusId={campusId} currency={currency} canEdit={!!canCreate} idleLocked={idleLocked} /> : null}
		{tabKey !== "courses" && tabKey !== "prices" && tabKey !== "legacy" ? <div style={{ ...ui.card, ...ui.filters }}>
			<div style={ui.field("280px")}>
				<span style={ui.caption}>Buscar</span>
				<input type="text" className="form-control" style={ui.input} placeholder="Nombre, código WA, SKU o escanear código de barras"
					value={query} disabled={idleLocked} onChange={(event) => { setQuery(event.target.value); setPage(1); }} />
			</div>
			<div style={ui.field("180px")}>
				<span style={ui.caption}>Dados de baja</span>
				<select className="form-select" style={ui.input} value={includeDeprecated ? "1" : "0"} disabled={idleLocked}
					onChange={(event) => { setIncludeDeprecated(event.target.value === "1"); setPage(1); }}>
					<option value="0">Ocultar</option>
					<option value="1">Incluir</option>
				</select>
			</div>
			<button type="button" className="btn btn-outline-success btn-sm rounded-pill" style={{ height: "34px" }} disabled={idleLocked}
				onClick={async () => { const response = await http.stockOpsService.exportProducts(campusId, { kinds: [tabKey], q: debouncedQuery.trim(), includeDeprecated: includeDeprecated ? 1 : 0 }); saveBlob(response.data, "productos.xlsx"); }}>Exportar Excel</button>
		</div> : null}

		{tabKey !== "courses" && tabKey !== "prices" && tabKey !== "legacy" ? <Pager page={page} lastPage={lastPage} total={total} unit="productos" busy={loading} onPage={setPage} /> : <div style={{ height: "8px" }} />}
		{tabKey !== "legacy" ? <div style={{ ...ui.card, overflowX: "auto" }}>
			{loading ? <Loading /> : error ? <div className="alert alert-danger" style={{ margin: "16px" }}>{error}</div>
				: tabKey === "prices" ? <PriceTray campusId={campusId} currency={currency} onChanged={() => { after(); http.inventoryService.catalogs(campusId).then((response) => setCatalogs(response.data)).catch(() => undefined); }} />
				: tabKey === "courses" ? (courseRows.length ? coursesTable() : <Empty text="No hay cursos en este plantel." />)
				: rows.length ? productsTable() : <Empty text="Sin productos en esta categoría. Usa «Nuevo producto» para darlos de alta." />}
		</div> : null}

		{formProduct !== undefined ? <ProductForm campusId={campusId} catalogs={catalogs} product={formProduct} defaultKind={tabKey === "courses" ? "kit" : tabKey}
			busy={modalBusy} error={modalError} onSave={saveProduct} onCancel={() => { if (!modalBusy) setFormProduct(undefined); }} /> : null}

		{sizesProduct ? <SizesModal catalogs={catalogs} product={sizesProduct} busy={modalBusy} error={modalError}
			onSave={saveSizes} onCancel={() => { if (!modalBusy) setSizesProduct(null); }} /> : null}

		{deprecating ? <ReasonPrompt
			title={deprecating.deprecatedAt ? "Reactivar producto" : "Dar de baja el producto"}
			description={deprecating.deprecatedAt
				? <><b>{deprecating.name}</b> volverá al catálogo y podrá venderse otra vez.</>
				: <><b>{deprecating.name}</b> sale del catálogo. No se borra: su historial y sus ventas anteriores se conservan.</>}
			confirmLabel={deprecating.deprecatedAt ? "Reactivar" : "Dar de baja"}
			confirmClass={deprecating.deprecatedAt ? "btn-primary" : "btn-warning"}
			busy={modalBusy} error={modalError} onConfirm={onDeprecate} onCancel={() => { if (!modalBusy) setDeprecating(null); }} /> : null}

		{detail ? <DetailModal campusId={campusId} product={detail} currency={currency} onClose={() => setDetail(null)} /> : null}

		{courseEditing ? <CourseKitsModal campusId={campusId} course={courseEditing} currency={currency} busy={modalBusy} error={modalError}
			onSave={async (kits) => {
				setModalBusy(true);
				try {
					await http.inventoryService.saveCourseKits(campusId, courseEditing.courseId, kits);
					setCourseEditing(null);
					await load();
				} catch (requestError: any) {
					setModalError(requestError?.response?.data?.message || "No se pudo guardar.");
				} finally { setModalBusy(false); }
			}}
			onCancel={() => { if (!modalBusy) setCourseEditing(null); }} /> : null}
	</div>;
};

// ------------------------------------------------------------------ modales auxiliares

const SizesModal: React.FC<{ catalogs: any; product: any; busy?: boolean; error?: string | null; onSave: (sizes: string[]) => void; onCancel: () => void }> = ({ catalogs, product, busy, error, onSave, onCancel }) => {
	const scale = catalogs?.sizeTypes?.[product.sizeType] || { label: "", values: [] };
	const [selected, setSelected] = useState<string[]>([]);
	return <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.45)", zIndex: 2000, display: "flex", alignItems: "center", justifyContent: "center", padding: "16px" }}
		onMouseDown={(event) => { if (event.target === event.currentTarget && !busy) onCancel(); }}>
		<div className="card" style={{ width: "100%", maxWidth: "520px", boxShadow: "0 10px 40px rgba(0,0,0,0.25)" }} role="dialog" aria-modal="true">
			<div className="card-body">
				<h4 style={{ marginBottom: "2px" }}>Tallas de {product.name}</h4>
				<div style={{ ...ui.small, marginBottom: "12px" }}>Escala: {scale.label}. Cada talla se cuenta por separado en bodega.</div>
				<div style={{ display: "flex", gap: "6px", flexWrap: "wrap" }}>
					{scale.values.map((size: any) => {
						const active = selected.includes(size.value);
						return <button key={size.value} type="button" disabled={busy}
							className={"btn btn-sm rounded-pill " + (active ? "btn-primary" : "btn-outline-secondary")}
							onClick={() => setSelected(active ? selected.filter((s) => s !== size.value) : [...selected, size.value])}>{size.label}</button>;
					})}
				</div>
				{error ? <div className="alert alert-danger" style={{ marginTop: "12px", padding: "8px 12px", fontSize: "14px" }}>{error}</div> : null}
				<div style={{ display: "flex", justifyContent: "flex-end", gap: "8px", marginTop: "14px" }}>
					<button type="button" className="btn btn-outline-secondary rounded-pill" disabled={busy} onClick={onCancel}>Cancelar</button>
					<button type="button" className="btn btn-primary rounded-pill" disabled={busy || selected.length < 1} onClick={() => onSave(selected)}>{busy ? "Creando…" : "Crear " + selected.length + " tallas"}</button>
				</div>
			</div>
		</div>
	</div>;
};

const DetailModal: React.FC<{ campusId: number; product: any; currency: string; onClose: () => void }> = ({ campusId, product, currency, onClose }) => <div
	style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.45)", zIndex: 2000, display: "flex", alignItems: "center", justifyContent: "center", padding: "16px" }}
	onMouseDown={(event) => { if (event.target === event.currentTarget) onClose(); }}>
	<div className="card" style={{ width: "100%", maxWidth: "680px", maxHeight: "90vh", overflowY: "auto", boxShadow: "0 10px 40px rgba(0,0,0,0.25)" }} role="dialog" aria-modal="true">
		<div className="card-body">
			<div style={{ display: "flex", gap: "12px" }}>
				<Thumb campusId={campusId} productId={product.id} has={product.hasPhoto} />
				<div style={{ flex: 1 }}>
					<h4 style={{ margin: 0 }}>{product.name}</h4>
					<div style={ui.small}>{product.waCode} · {product.kindLabel} · {currency}{Number(product.basePrice || 0).toFixed(2)}</div>
				</div>
				<button type="button" className={ui.btn} onClick={onClose}>Cerrar</button>
			</div>
			{product.description ? <p style={{ fontSize: "13px", marginTop: "10px" }}>{product.description}</p> : null}

			{(product.availability || []).length ? <>
				<div style={{ ...ui.caption, marginTop: "12px" }}>Disponibilidad</div>
				{product.availability.map((row: any) => <div key={row.warehouseId} style={{ fontSize: "13px", padding: "4px 0", borderBottom: "1px solid var(--wa-papel-2)" }}>
					<b>{row.available}</b> en {row.warehouse}{row.limitedBy ? <span style={ui.small}> · limita: {row.limitedBy}</span> : null}
				</div>)}
			</> : null}

			{(product.version?.components || []).length ? <>
				<div style={{ ...ui.caption, marginTop: "12px" }}>Componentes ({product.version.label})</div>
				{product.version.components.map((component: any) => <div key={component.id} style={{ fontSize: "13px", padding: "4px 0", borderBottom: "1px solid var(--wa-papel-2)" }}>
					{component.name} × {component.quantity}
					{component.substitutes.length ? <span style={ui.small}> · o {component.substitutes.map((s: any) => s.name).join(", ")}</span> : null}
					{component.sizeAtDelivery ? <span style={ui.small}> · talla al entregar</span> : null}
				</div>)}
			</> : null}

			{(product.variants || []).length ? <>
				<div style={{ ...ui.caption, marginTop: "12px" }}>Tallas y existencia</div>
				{product.variants.map((variant: any) => <div key={variant.id} style={{ fontSize: "13px", padding: "4px 0", borderBottom: "1px solid var(--wa-papel-2)" }}>
					{variant.sizeValue}: <b>{variant.onHand}</b> <span style={ui.small}>{variant.stockByWarehouse.filter((w: any) => w.onHand).map((w: any) => w.code + " " + w.onHand).join(" · ")}</span>
				</div>)}
			</> : null}

			{(product.stockByWarehouse || []).length && product.trackStock ? <>
				<div style={{ ...ui.caption, marginTop: "12px" }}>Existencia por bodega</div>
				{product.stockByWarehouse.map((row: any) => <div key={row.warehouseId} style={{ fontSize: "13px", padding: "4px 0", borderBottom: "1px solid var(--wa-papel-2)" }}>{row.name}: <b>{row.onHand}</b></div>)}
			</> : null}

			{(product.courses || []).length ? <>
				<div style={{ ...ui.caption, marginTop: "12px" }}>Cursos</div>
				<div style={{ fontSize: "13px" }}>{product.courses.map((c: any) => c.course).join(" · ")}</div>
			</> : null}
		</div>
	</div>
</div>;

const CourseKitsModal: React.FC<{ campusId: number; course: any; currency: string; busy?: boolean; error?: string | null; onSave: (kits: any[]) => void; onCancel: () => void }> = ({ campusId, course, currency, busy, error, onSave, onCancel }) => {
	const [kits, setKits] = useState<any[]>(course.draft || []);
	return <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.45)", zIndex: 2000, display: "flex", alignItems: "center", justifyContent: "center", padding: "16px" }}
		onMouseDown={(event) => { if (event.target === event.currentTarget && !busy) onCancel(); }}>
		<div className="card" style={{ width: "100%", maxWidth: "620px", maxHeight: "90vh", overflowY: "auto", boxShadow: "0 10px 40px rgba(0,0,0,0.25)" }} role="dialog" aria-modal="true">
			<div className="card-body">
				<h4 style={{ marginBottom: "2px" }}>Kits de {course.courseBrief}</h4>
				<div style={{ ...ui.small, marginBottom: "12px" }}>En el orden en que se ofrecen. Cuando abre un horario de este curso, sus alumnos ya tienen qué kit se les ofrece.</div>
				<div style={{ marginBottom: "10px" }}>
					<span style={ui.caption}>Agregar kit</span>
					<ProductPicker campusId={campusId} kinds={["kit", "uniform"]} disabled={busy} excludeIds={kits.map((k) => k.kitArticleId)}
						onPick={(picked) => setKits([...kits, { kitArticleId: picked.id, name: picked.name, price: picked.price, required: kits.length === 0, tierRank: kits.length + 1, fromLevelNumber: null }])} />
				</div>
				{kits.length < 1 ? <div style={{ ...ui.small, padding: "10px 0" }}>Sin kits asignados.</div>
					: kits.map((kit, index) => <div key={kit.kitArticleId} style={{ display: "flex", gap: "8px", alignItems: "center", padding: "8px 0", borderBottom: "1px solid var(--wa-papel-2)", flexWrap: "wrap" }}>
						<span style={{ fontWeight: 600, flex: "1 1 180px" }}>{index + 1}. {kit.name} <span style={ui.small}>{currency}{Number(kit.price || 0).toFixed(2)}</span></span>
						<select className="form-select" style={{ ...ui.input, width: "150px" }} value={kit.required ? "1" : "0"} disabled={busy}
							onChange={(event) => setKits(kits.map((k, i) => i === index ? { ...k, required: event.target.value === "1" } : k))}>
							<option value="1">Obligatorio</option>
							<option value="0">Recomendado</option>
						</select>
						<input type="number" min={1} className="form-control" style={{ ...ui.input, width: "130px" }} placeholder="Desde módulo" value={kit.fromLevelNumber ?? ""} disabled={busy}
							onChange={(event) => setKits(kits.map((k, i) => i === index ? { ...k, fromLevelNumber: event.target.value === "" ? null : Number(event.target.value) } : k))} />
						<button type="button" className="btn btn-outline-secondary btn-sm rounded-pill waOroTexto" disabled={busy} onClick={() => setKits(kits.filter((_, i) => i !== index))}>Quitar</button>
					</div>)}
				{error ? <div className="alert alert-danger" style={{ marginTop: "12px", padding: "8px 12px", fontSize: "14px" }}>{error}</div> : null}
				<div style={{ display: "flex", justifyContent: "flex-end", gap: "8px", marginTop: "14px" }}>
					<button type="button" className="btn btn-outline-secondary rounded-pill" disabled={busy} onClick={onCancel}>Cancelar</button>
					<button type="button" className="btn btn-primary rounded-pill" disabled={busy} onClick={() => onSave(kits.map((kit, index) => ({ ...kit, tierRank: index + 1 })))}>{busy ? "Guardando…" : "Guardar"}</button>
				</div>
			</div>
		</div>
	</div>;
};

/** Bandeja de Super Admin: precios de venta sugeridos por el equipo, con el costo de compra y el margen que dejarían. */
const PriceTray: React.FC<{ campusId: number; currency: string; onChanged: () => void }> = ({ campusId, currency, onChanged }) => {
	const http = HttpManager.getInstance().inventoryService;
	const [rows, setRows] = useState<any[] | null>(null);
	const [prices, setPrices] = useState<Record<number, string>>({});
	const [busyId, setBusyId] = useState<number | null>(null);
	const [error, setError] = useState<string | null>(null);
	const [rejecting, setRejecting] = useState<any | null>(null);
	const load = useCallback(() => {
		http.priceRequests(campusId).then((response) => { setRows(response.data); setPrices({}); }).catch((requestError) => { setRows([]); setError(requestError?.response?.data?.errors?.error?.[0] || "No se pudo cargar la bandeja."); });
	}, [campusId]);
	useEffect(() => { load(); }, [load]);
	const pct = (value: number | null) => value === null ? "-" : (value * 100).toFixed(1) + "%";
	const marginOf = (row: any, price: number) => row.cost === null || price <= 0 ? null : (price - row.cost) / price;
	const approve = async (row: any) => {
		setBusyId(row.id);
		setError(null);
		try {
			const typed = prices[row.id];
			await http.approvePrice(campusId, row.id, typed !== undefined && typed !== "" ? Number(typed) : null, "");
			load();
			onChanged();
		} catch (requestError: any) {
			setError(requestError?.response?.data?.errors?.error?.[0] || "No se pudo aprobar.");
		} finally {
			setBusyId(null);
		}
	};
	if (rows === null) return <Loading />;
	return <>
		{error ? <div className="alert alert-danger" style={{ margin: "10px" }}>{error}</div> : null}
		{rows.length < 1 ? <Empty text="No hay precios por aprobar." /> : <table style={{ width: "100%", borderCollapse: "collapse" }}>
			<thead><tr>
				<th style={ui.th}>Producto</th>
				<th style={ui.th}>Sugirió</th>
				<th style={{ ...ui.th, ...ui.right }}>Precio hoy</th>
				<th style={{ ...ui.th, ...ui.right }}>Costo de compra</th>
				<th style={{ ...ui.th, width: "140px" }}>Precio a aprobar</th>
				<th style={{ ...ui.th, ...ui.right }}>Margen bruto</th>
				<th style={{ ...ui.th, ...ui.right }}>Precio al objetivo</th>
				<th style={ui.th}></th>
			</tr></thead>
			<tbody>{rows.map((row) => {
				const typed = prices[row.id];
				const price = typed !== undefined && typed !== "" ? Number(typed) : row.suggestedPrice;
				const margin = marginOf(row, price);
				return <tr key={row.id}>
					<td style={ui.td}><div style={{ fontWeight: 600 }}>{row.name}</div><div style={ui.tiny}>{row.waCode} · {row.kind}{row.isNew ? " · nuevo" : ""}</div>{row.note ? <div style={ui.small}>{row.note}</div> : null}</td>
					<td style={ui.td}>{row.requestedBy}<div style={ui.tiny}>{row.requestedAt}</div></td>
					<td style={{ ...ui.td, ...ui.right }}>{row.currentPrice !== null ? currency + Number(row.currentPrice).toFixed(2) : "-"}</td>
					<td style={{ ...ui.td, ...ui.right }}>{row.cost !== null ? currency + Number(row.cost).toFixed(2) : <span style={ui.small}>sin compra</span>}</td>
					<td style={ui.td}><input type="number" min={0} step="0.01" className="form-control" style={ui.input} value={typed !== undefined ? typed : String(row.suggestedPrice)}
						disabled={busyId !== null} onChange={(event) => setPrices({ ...prices, [row.id]: event.target.value })} /></td>
					<td style={{ ...ui.td, ...ui.right, fontWeight: 700, color: margin === null ? "var(--wa-apagado)" : margin < Math.max(0.2, row.target || 0) ? "var(--wa-oro)" : "var(--wa-verde)" }}>{pct(margin)}</td>
					<td style={{ ...ui.td, ...ui.right }}>{row.priceForTarget !== null ? <button type="button" className="btn btn-link btn-sm" style={{ padding: 0 }} disabled={busyId !== null}
						onClick={() => setPrices({ ...prices, [row.id]: String(row.priceForTarget) })}>{currency}{Number(row.priceForTarget).toFixed(2)}</button> : "-"}
						{row.target !== null ? <div style={ui.tiny}>objetivo {pct(row.target)}</div> : null}</td>
					<td style={ui.td}><div style={{ display: "flex", gap: "6px", justifyContent: "flex-end" }}>
						<button type="button" className={ui.btn} disabled={busyId !== null} onClick={() => setRejecting(row)}>Rechazar</button>
						<button type="button" className={ui.btnPrimary} disabled={busyId !== null || !(price > 0)} onClick={() => approve(row)}>{busyId === row.id ? "…" : "Aprobar"}</button>
					</div></td>
				</tr>;
			})}</tbody>
		</table>}
		{rejecting ? <ReasonPrompt title={"Rechazar precio de " + rejecting.name} label="Motivo" confirmLabel="Rechazar" confirmClass="btn-primary"
			busy={busyId !== null} onCancel={() => setRejecting(null)}
			onConfirm={async (reason) => {
				setBusyId(rejecting.id);
				try { await http.rejectPrice(campusId, rejecting.id, reason); setRejecting(null); load(); onChanged(); }
				catch (requestError: any) { setError(requestError?.response?.data?.errors?.error?.[0] || "No se pudo rechazar."); }
				finally { setBusyId(null); }
			}} /> : null}
	</>;
};

export default Productos;
